/**
 * live-preview-tunnel: the end-to-end channel between a viewing browser and the daemon that
 * serves an agent's local page (docs/security-multitenant.md "Live preview tunnel").
 *
 * Keys. The browser (app origin) and the daemon each make an ephemeral P-256 ECDH pair per preview
 * session. The browser's public key travels inside the device-signed, content-sealed
 * `preview.open` command; the daemon's comes back in the sealed command result. Both sides derive
 * two AES-256-GCM keys (browser→daemon, daemon→browser) with HKDF over the ECDH secret, salted by
 * a hash of the whole handshake transcript (account, machine, preview id, origin label, both public
 * keys). The keys are non-extractable; the app hands them (structured-cloned CryptoKeys) to that
 * preview's origin only. Account keys never leave the app origin.
 *
 * Frames. `version(1) | type(1) | streamId(16) | seq(u32 BE) | AES-GCM(payload)`. The nonce is
 * `streamId[0..8] | seq`, unique per direction key because stream ids are random and never reused
 * within a session. The AAD binds the preview id and the whole header, so a frame cannot move to
 * another preview, stream, position or type. Receivers demand strict per-stream sequence order, so
 * a relay can drop frames (the stream then stalls/fails) but cannot replay, reorder or splice them.
 *
 * Flow control. Data frames (bodies and WebSocket messages) consume a per-stream credit window;
 * the receiver returns CREDIT frames as the consumer takes the bytes. A slow consumer (upstream
 * dev server, a phone on a bad network) therefore bounds memory on both ends.
 */
import { concat, copy, fromUtf8, randomBytes, toHex, utf8, } from './bytes.js';
import { sha256 } from './hash.js';
export const PREVIEW_TUNNEL_VERSION = 1;
export const PREVIEW_STREAM_ID_BYTES = 16;
export const PREVIEW_HEADER_BYTES = 2 + PREVIEW_STREAM_ID_BYTES + 4;
/** Largest plaintext in one data frame; bodies are chunked to this. */
export const PREVIEW_CHUNK_BYTES = 64 * 1024;
/** Default per-stream credit window (must stay ≥ 2 × PREVIEW_CHUNK_BYTES). */
export const PREVIEW_WINDOW_BYTES = 1024 * 1024;
/** Upper bound for any frame on the wire (header + chunk + tag + JSON heads). */
export const PREVIEW_MAX_FRAME_BYTES = PREVIEW_CHUNK_BYTES + 1024;
export const PreviewFrameType = {
    REQ_HEAD: 1,
    REQ_BODY: 2,
    REQ_END: 3,
    RES_HEAD: 4,
    RES_BODY: 5,
    RES_END: 6,
    ERROR: 7,
    WS_OPEN: 8,
    WS_OPENED: 9,
    WS_TEXT: 10,
    WS_BINARY: 11,
    WS_CLOSE: 12,
    CREDIT: 13,
    CANCEL: 14,
};
const DATA_TYPES = new Set([
    PreviewFrameType.REQ_BODY,
    PreviewFrameType.RES_BODY,
    PreviewFrameType.WS_TEXT,
    PreviewFrameType.WS_BINARY,
]);
/** Types that open a stream (only the browser opens streams). */
const OPENING_TYPES = new Set([
    PreviewFrameType.REQ_HEAD,
    PreviewFrameType.WS_OPEN,
]);
const BROWSER_SENDS = new Set([
    PreviewFrameType.REQ_HEAD,
    PreviewFrameType.REQ_BODY,
    PreviewFrameType.REQ_END,
    PreviewFrameType.WS_OPEN,
    PreviewFrameType.WS_TEXT,
    PreviewFrameType.WS_BINARY,
    PreviewFrameType.WS_CLOSE,
    PreviewFrameType.CREDIT,
    PreviewFrameType.CANCEL,
]);
const DAEMON_SENDS = new Set([
    PreviewFrameType.RES_HEAD,
    PreviewFrameType.RES_BODY,
    PreviewFrameType.RES_END,
    PreviewFrameType.ERROR,
    PreviewFrameType.WS_OPENED,
    PreviewFrameType.WS_TEXT,
    PreviewFrameType.WS_BINARY,
    PreviewFrameType.WS_CLOSE,
    PreviewFrameType.CREDIT,
]);
export function encodePreviewJson(value) {
    return utf8(JSON.stringify(value));
}
export function decodePreviewJson(bytes) {
    return JSON.parse(fromUtf8(bytes));
}
const subtle = () => globalThis.crypto.subtle;
export async function generatePreviewEphemeral() {
    const pair = (await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, [
        'deriveBits',
    ]));
    const raw = new Uint8Array(await subtle().exportKey('raw', pair.publicKey));
    return { privateKey: pair.privateKey, publicRaw: raw };
}
function lengthPrefixed(...parts) {
    return concat(...parts.flatMap((p) => {
        const n = new Uint8Array(4);
        new DataView(n.buffer).setUint32(0, p.length);
        return [n, p];
    }));
}
/** Hash of the handshake transcript: both keys are bound to every field. */
export async function previewTranscriptHash(c) {
    return sha256(lengthPrefixed(utf8('viko/preview-tunnel/v1'), utf8(c.accountId), utf8(c.machineId), utf8(c.previewId), utf8(c.label), c.browserPublic, c.daemonPublic));
}
export async function derivePreviewTunnelKeys(o) {
    if (o.peerPublic.length !== 65 || o.peerPublic[0] !== 4)
        throw new Error('preview: bad peer public key');
    // importKey validates the point is on the curve.
    const peer = await subtle().importKey('raw', copy(o.peerPublic), { name: 'ECDH', namedCurve: 'P-256' }, false, []);
    const secret = new Uint8Array(await subtle().deriveBits({ name: 'ECDH', public: peer }, o.privateKey, 256));
    const ikm = await subtle().importKey('raw', secret, 'HKDF', false, ['deriveKey']);
    secret.fill(0);
    const salt = await previewTranscriptHash(o.context);
    const derive = (dir) => subtle().deriveKey({ name: 'HKDF', hash: 'SHA-256', salt, info: utf8(`viko/preview-tunnel/v1/${dir}`) }, ikm, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    const [c2d, d2c] = await Promise.all([derive('c2d'), derive('d2c')]);
    return o.role === 'browser' ? { send: c2d, recv: d2c } : { send: d2c, recv: c2d };
}
function aad(previewId, header) {
    return concat(utf8(`viko/preview-frame/v1\u0000${previewId}\u0000`), header);
}
function header(type, streamId, seq) {
    if (streamId.length !== PREVIEW_STREAM_ID_BYTES)
        throw new Error('preview: bad stream id');
    if (!Number.isInteger(seq) || seq < 0 || seq > 0xffffffff)
        throw new Error('preview: bad seq');
    const h = new Uint8Array(PREVIEW_HEADER_BYTES);
    h[0] = PREVIEW_TUNNEL_VERSION;
    h[1] = type;
    h.set(streamId, 2);
    new DataView(h.buffer).setUint32(2 + PREVIEW_STREAM_ID_BYTES, seq);
    return h;
}
function nonce(streamId, seq) {
    const n = new Uint8Array(12);
    n.set(streamId.subarray(0, 8), 0);
    new DataView(n.buffer).setUint32(8, seq);
    return n;
}
export async function sealPreviewFrame(key, previewId, f) {
    const h = header(f.type, f.streamId, f.seq);
    const ct = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv: nonce(f.streamId, f.seq), additionalData: aad(previewId, h) }, key, copy(f.payload ?? new Uint8Array())));
    return concat(h, ct);
}
export async function openPreviewFrame(key, previewId, frame) {
    if (frame.length < PREVIEW_HEADER_BYTES + 16 || frame.length > PREVIEW_MAX_FRAME_BYTES + 64)
        throw new Error('preview: bad frame length');
    if (frame[0] !== PREVIEW_TUNNEL_VERSION)
        throw new Error('preview: unsupported frame version');
    const h = copy(frame.subarray(0, PREVIEW_HEADER_BYTES));
    const streamId = copy(h.subarray(2, 2 + PREVIEW_STREAM_ID_BYTES));
    const seq = new DataView(h.buffer).getUint32(2 + PREVIEW_STREAM_ID_BYTES);
    const type = h[1];
    let pt;
    try {
        pt = new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv: nonce(streamId, seq), additionalData: aad(previewId, h) }, key, copy(frame.subarray(PREVIEW_HEADER_BYTES))));
    }
    catch {
        throw new Error('preview: frame authentication failed');
    }
    return { type, streamId, seq, payload: copy(pt) };
}
export class PreviewStream {
    channel;
    id;
    autoCredit;
    /** Frames received on this stream (in order, after authentication). */
    onFrame;
    /** Called once when the stream is closed locally (end, error, cancel, channel close). */
    onClose;
    sendSeq = 0;
    /** Bytes sent but not yet credited by the peer. */
    inflight = 0;
    waiters = [];
    /** Bytes delivered to our consumer but not yet credited back. */
    uncredited = 0;
    closed = false;
    constructor(channel, id, 
    /** false: the consumer calls `consumed(n)` itself (backpressure onto the peer). */
    autoCredit = true) {
        this.channel = channel;
        this.id = id;
        this.autoCredit = autoCredit;
    }
    get idHex() {
        return toHex(this.id);
    }
    /** Send one frame; data frames wait for credit. Chunk bodies with `sendData`. */
    async send(type, payload) {
        if (this.closed)
            throw new Error('preview: stream closed');
        if (DATA_TYPES.has(type)) {
            const n = payload?.length ?? 0;
            if (n > PREVIEW_CHUNK_BYTES)
                throw new Error('preview: chunk too large');
            while (this.inflight > 0 && this.inflight + n > this.channel.window) {
                await new Promise((r) => this.waiters.push(r));
                if (this.closed)
                    throw new Error('preview: stream closed');
            }
            this.inflight += n;
        }
        const seq = this.sendSeq++;
        await this.channel.transmitFrame(type, this.id, seq, payload);
    }
    /** Send bytes as ≤ PREVIEW_CHUNK_BYTES data frames. */
    async sendData(type, bytes) {
        if (bytes.length === 0)
            return;
        for (let i = 0; i < bytes.length; i += PREVIEW_CHUNK_BYTES)
            await this.send(type, bytes.subarray(i, Math.min(bytes.length, i + PREVIEW_CHUNK_BYTES)));
    }
    /** Tell the peer `n` delivered bytes were consumed (manual-credit streams). */
    consumed(n) {
        if (this.closed || n <= 0)
            return;
        this.uncredited += n;
        if (this.uncredited >= Math.floor(this.channel.window / 4))
            this.flushCredit();
    }
    /** Grant any pending credit now (end of a burst). */
    flushCredit() {
        if (this.closed || this.uncredited <= 0)
            return;
        const n = this.uncredited;
        this.uncredited = 0;
        const b = new Uint8Array(4);
        new DataView(b.buffer).setUint32(0, n);
        void this.send(PreviewFrameType.CREDIT, b).catch(() => { });
    }
    /** @internal */
    deliver(type, payload) {
        if (type === PreviewFrameType.CREDIT) {
            if (payload.length !== 4)
                throw new Error('preview: bad credit');
            const n = new DataView(payload.buffer, payload.byteOffset, 4).getUint32(0);
            this.inflight = Math.max(0, this.inflight - n);
            const w = this.waiters;
            this.waiters = [];
            for (const r of w)
                r();
            return;
        }
        this.onFrame?.(type, payload);
        if (DATA_TYPES.has(type) && this.autoCredit)
            this.consumed(payload.length);
    }
    /** Drop local state (no frame is sent). */
    close() {
        if (this.closed)
            return;
        this.closed = true;
        const w = this.waiters;
        this.waiters = [];
        for (const r of w)
            r();
        this.channel.forget(this);
        try {
            this.onClose?.();
        }
        catch { }
    }
}
/**
 * One preview session's multiplexed, encrypted channel. Sends are serialized so sequence numbers
 * leave in order; receives are processed strictly in arrival order.
 */
export class PreviewChannel {
    o;
    window;
    streams = new Map();
    /** Every stream id ever seen (ids are never reused within a session). */
    used = new Set();
    recvSeq = new Map();
    sendChain = Promise.resolve();
    recvChain = Promise.resolve();
    closed = false;
    constructor(o) {
        this.o = o;
        this.window = o.window ?? PREVIEW_WINDOW_BYTES;
        if (this.window < 2 * PREVIEW_CHUNK_BYTES)
            throw new Error('preview: window too small');
    }
    get previewId() {
        return this.o.previewId;
    }
    get openStreams() {
        return this.streams.size;
    }
    /** Browser side: a new stream with a fresh random id. */
    open(autoCredit = true) {
        if (this.o.role !== 'browser')
            throw new Error('preview: only the browser opens streams');
        if (this.closed)
            throw new Error('preview: channel closed');
        let id;
        do
            id = randomBytes(PREVIEW_STREAM_ID_BYTES);
        while (this.used.has(toHex(id)));
        const s = new PreviewStream(this, id, autoCredit);
        this.used.add(s.idHex);
        this.streams.set(s.idHex, s);
        return s;
    }
    /** @internal */
    transmitFrame(type, streamId, seq, payload) {
        const allowed = this.o.role === 'browser' ? BROWSER_SENDS : DAEMON_SENDS;
        if (!allowed.has(type))
            return Promise.reject(new Error('preview: frame type not allowed'));
        // Frames queued before close() still leave (a final ERROR/END); nothing new is accepted.
        if (this.closed)
            return Promise.reject(new Error('preview: channel closed'));
        const p = this.sendChain.then(async () => {
            const f = await sealPreviewFrame(this.o.keys.send, this.o.previewId, {
                type,
                streamId,
                seq,
                ...(payload ? { payload } : {}),
            });
            this.o.transmit(f);
        });
        this.sendChain = p.catch(() => { });
        return p;
    }
    /** Ciphertext in. Violations close the channel (fail closed). */
    receive(frame) {
        const bytes = copy(frame);
        const p = this.recvChain.then(() => this.receiveOne(bytes));
        this.recvChain = p.catch(() => { });
        return p;
    }
    async receiveOne(frame) {
        if (this.closed)
            return;
        let f;
        try {
            f = await openPreviewFrame(this.o.keys.recv, this.o.previewId, frame);
        }
        catch (e) {
            return this.violation(e.message);
        }
        const peerSends = this.o.role === 'browser' ? DAEMON_SENDS : BROWSER_SENDS;
        if (!peerSends.has(f.type))
            return this.violation('preview: unexpected frame type');
        const id = toHex(f.streamId);
        const expected = this.recvSeq.get(id) ?? 0;
        if (f.seq !== expected)
            return this.violation('preview: frame out of order or replayed');
        let s = this.streams.get(id);
        if (!s) {
            if (this.o.role !== 'daemon' || f.seq !== 0 || !OPENING_TYPES.has(f.type)) {
                // Late frames for a stream this side already closed are dropped quietly.
                if (this.used.has(id) && f.seq !== 0) {
                    this.recvSeq.set(id, expected + 1);
                    return;
                }
                return this.violation('preview: unknown stream');
            }
            if (this.used.has(id))
                return this.violation('preview: stream id reused');
            if (this.streams.size >= (this.o.maxStreams ?? 256))
                return this.violation('preview: too many streams');
            s = new PreviewStream(this, f.streamId);
            this.used.add(id);
            this.streams.set(id, s);
            this.recvSeq.set(id, 1);
            this.o.onStream?.(s, f.type, f.payload);
            return;
        }
        this.recvSeq.set(id, expected + 1);
        try {
            s.deliver(f.type, f.payload);
        }
        catch (e) {
            return this.violation(e.message);
        }
    }
    violation(reason) {
        this.close();
        this.o.onViolation?.(reason);
    }
    /** @internal */
    forget(s) {
        this.streams.delete(s.idHex);
    }
    /** Resolves once every frame received so far has been processed. */
    settled() {
        return this.recvChain;
    }
    /** Resolves once every frame queued so far has been handed to `transmit`. */
    drained() {
        return this.sendChain;
    }
    close() {
        if (this.closed)
            return;
        this.closed = true;
        for (const s of [...this.streams.values()])
            s.close();
        this.streams.clear();
    }
}
// ---- WebSocket messages over data frames ------------------------------------------------------
/** Largest WebSocket message either side reassembles. */
export const PREVIEW_MAX_MESSAGE_BYTES = 16 * 1024 * 1024;
/** A message as one or more data frames: payload = `final(1) | bytes`. */
export async function sendPreviewMessage(stream, type, bytes) {
    if (bytes.length > PREVIEW_MAX_MESSAGE_BYTES)
        throw new Error('preview: message too large');
    const step = PREVIEW_CHUNK_BYTES - 1;
    let i = 0;
    do {
        const end = Math.min(bytes.length, i + step);
        const part = new Uint8Array(1 + end - i);
        part[0] = end === bytes.length ? 1 : 0;
        part.set(bytes.subarray(i, end), 1);
        await stream.send(type, part);
        i = end;
    } while (i < bytes.length);
}
/** Reassembles fragmented messages; returns the whole message on its final fragment. */
export class PreviewMessageAssembler {
    parts = [];
    size = 0;
    type;
    push(type, payload) {
        if (payload.length < 1)
            throw new Error('preview: empty message fragment');
        if (this.type !== undefined && this.type !== type)
            throw new Error('preview: interleaved message types');
        this.type = type;
        this.size += payload.length - 1;
        if (this.size > PREVIEW_MAX_MESSAGE_BYTES)
            throw new Error('preview: message too large');
        this.parts.push(payload.subarray(1));
        if (payload[0] !== 1)
            return null;
        const bytes = concat(...this.parts);
        this.parts = [];
        this.size = 0;
        this.type = undefined;
        return { type, bytes };
    }
}
//# sourceMappingURL=preview-tunnel.js.map